import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Badge,
  Tooltip,
  Popconfirm,
  message,
  Tabs,
  Divider,
  Avatar,
  List,
  Empty,
  Alert,
  DatePicker,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  TrophyOutlined,
  FireOutlined,
  StarOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  TeamOutlined,
  CalendarOutlined,
  FlagOutlined,
  BulbOutlined,
  RocketOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  LinkOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { getUserIdAsNumber } from '../utils/cognitoUtils';
import apiService from '../services/api';
import TaskFeedbackModal from './TaskFeedbackModal';
import { getLeadCategoryStyle } from '../utils/leadCategoryColors';
import './TaskMaster.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface Task {
  id: number;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  category: string;
  task_type: 'request' | 'challenge' | 'mission';
  difficulty_level: 'easy' | 'normal' | 'hard' | 'extreme';
  points_reward: number;
  time_limit?: string;
  response_text?: string;
  feedback?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  created_by: number;
  assigned_to: number;
  lead_id?: number;
  creator_name?: string;
  assignee_name?: string;
  completer_name?: string;
  lead_name?: string;
  lead_phone?: string;
}

interface TaskStatistics {
  assigned_to_me: {
    total: number;
    pending: number;
    in_progress: number;
    completed: number;
    overdue: number;
  };
  created_by_me: {
    total: number;
    pending: number;
    in_progress: number;
    completed: number;
  };
  points_earned: number;
  tasks_completed_today: number;
}

interface TaskMasterProps {
  preSelectedLead?: any;
  onTaskCreated?: () => void;
  isModal?: boolean;
}

const TaskMaster: React.FC<TaskMasterProps> = ({ preSelectedLead, onTaskCreated, isModal = false }) => {
  const { user } = useCognitoAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statistics, setStatistics] = useState<TaskStatistics | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  
  // Feedback modal state
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [feedbackActionType, setFeedbackActionType] = useState<'complete' | 'cancel' | 'start_progress'>('complete');
  const [feedbackTaskId, setFeedbackTaskId] = useState<number | null>(null);
  const [feedbackTaskTitle, setFeedbackTaskTitle] = useState<string>('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    task_type: '',
    assigned_to_me: false,
    created_by_me: false
  });

       const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  
  // Event handlers with stopPropagation
  const handleCreateModalClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCreateModalVisible(false);
    createForm.resetFields();
    if (onTaskCreated && isModal) {
      onTaskCreated();
    }
  };
  
  const handleEditModalClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditModalVisible(false);
    setSelectedTask(null);
    editForm.resetFields();
  };
  
  const handleViewModalClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setViewModalVisible(false);
    setSelectedTask(null);
  };

  const themeColors = {
    primary: '#ff6b35', // TaskMaster orange
    secondary: '#f7931e', // Golden
    success: '#52c41a',
    warning: '#faad14',
    danger: '#ff4d4f',
    info: '#13c2c2'
  };

  useEffect(() => {
    fetchTasks();
    fetchStatistics();
    fetchAvailableUsers();
    fetchAllUsers();
    fetchLeads();
  }, [filters]);

  // Effect to handle preSelectedLead and open create modal
  useEffect(() => {
    if (preSelectedLead && isModal) {
      setCreateModalVisible(true);
      // Pre-fill the form with the selected lead
      createForm.setFieldsValue({
        lead_id: preSelectedLead.id
      });
    }
  }, [preSelectedLead, isModal, createForm]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.category) params.append('category', filters.category);
      if (filters.task_type) params.append('task_type', filters.task_type);
      if (filters.assigned_to_me) params.append('assigned_to_me', 'true');
      if (filters.created_by_me) params.append('created_by_me', 'true');

      const response = await apiService.getTasks(params.toString());
      if (response.success) {
        setTasks(response.tasks);
      }
    } catch (error) {
      message.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await apiService.getTaskStatistics();
      if (response.success) {
        setStatistics(response.statistics);
      }
    } catch (error) {
      message.error('Erro ao carregar estatísticas');
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await apiService.getAvailableUsers();
      if (response.success) {
        setAvailableUsers(response.users);
      } else {
        message.error('Erro ao carregar utilizadores');
      }
    } catch (error) {
      message.error('Erro ao carregar utilizadores');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const users = await apiService.getUsers();
      setAllUsers(users);
    } catch (error) {
      message.error('Erro ao carregar todos os utilizadores');
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await apiService.getLeads();
      setLeads(response || []);
    } catch (error) {
      message.error('Erro ao carregar leads');
    }
  };

  const handleCreateTask = async (values: any) => {
    try {
      setCreateLoading(true);
      
      const taskData = {
        ...values,
        created_by: user?.id,
        status: 'pending'
      };
      
      const response = await apiService.createTask(taskData);
      
      if (response.success) {
        message.success('Tarefa criada com sucesso!');
        setCreateModalVisible(false);
        createForm.resetFields();
        
        // Call the onTaskCreated callback if provided
        if (onTaskCreated) {
          onTaskCreated();
        }
        
        // Immediately refresh user data and tasks
        fetchAvailableUsers();
        fetchAllUsers();
        fetchTasks();
        fetchStatistics();
      } else {
        message.error(response.message || 'Erro ao criar tarefa');
      }
    } catch (error) {
      message.error('Erro ao criar tarefa');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateTask = async (values: any) => {
    if (!selectedTask) return;
    try {
      const response = await apiService.updateTask(selectedTask.id, values);
      if (response.success) {
        message.success('Tarefa atualizada com sucesso!');
        setEditModalVisible(false);
        setSelectedTask(null);
        editForm.resetFields();
        fetchTasks();
        fetchStatistics();
      }
    } catch (error) {
      message.error('Erro ao atualizar tarefa');
    }
  };

  const handleCompleteTask = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      // Check if user has permission to complete this task
      if (task.assigned_to !== getUserIdAsNumber(user) && task.created_by !== getUserIdAsNumber(user)) {
        message.error('Não tem permissão para concluir esta tarefa');
        return;
      }
      
      setFeedbackTaskId(taskId);
      setFeedbackTaskTitle(task.title);
      setFeedbackActionType('complete');
      setFeedbackModalVisible(true);
    }
  };

  const handleCancelTask = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      // Check if user has permission to cancel this task
      if (task.assigned_to !== getUserIdAsNumber(user) && task.created_by !== getUserIdAsNumber(user)) {
        message.error('Não tem permissão para cancelar esta tarefa');
        return;
      }
      
      setFeedbackTaskId(taskId);
      setFeedbackTaskTitle(task.title);
      setFeedbackActionType('cancel');
      setFeedbackModalVisible(true);
    }
  };

  const handleStartProgress = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      // Check if user has permission to start progress on this task
      if (task.assigned_to !== getUserIdAsNumber(user) && task.created_by !== getUserIdAsNumber(user)) {
        message.error('Não tem permissão para iniciar progresso nesta tarefa');
        return;
      }
      
      setFeedbackTaskId(taskId);
      setFeedbackTaskTitle(task.title);
      setFeedbackActionType('start_progress');
      setFeedbackModalVisible(true);
    }
  };

  const handleFeedbackSubmit = async (feedback: string) => {
    if (!feedbackTaskId) return;
    
    setFeedbackLoading(true);
    try {
      if (feedbackActionType === 'complete') {
        const data = { feedback: feedback };
        const response = await apiService.completeTask(feedbackTaskId, data);
        if (response.success) {
          message.success('Tarefa concluída com sucesso!');
          fetchTasks();
          fetchStatistics();
        } else {
          message.error(response.message || 'Erro ao concluir tarefa');
        }
      } else if (feedbackActionType === 'start_progress') {
        const data = { status: 'in_progress', feedback: feedback };
        const response = await apiService.updateTask(feedbackTaskId, data);
        if (response.success) {
          message.success('Progresso iniciado com sucesso!');
          fetchTasks();
          fetchStatistics();
        } else {
          message.error(response.message || 'Erro ao iniciar progresso');
        }
      } else {
        const data = { status: 'cancelled', feedback: feedback };
        const response = await apiService.updateTask(feedbackTaskId, data);
        if (response.success) {
          message.success('Tarefa cancelada com sucesso!');
          fetchTasks();
          fetchStatistics();
        } else {
          message.error(response.message || 'Erro ao cancelar tarefa');
        }
      }
      
      // Close feedback modal
      setFeedbackModalVisible(false);
      setFeedbackTaskId(null);
      setFeedbackTaskTitle('');
      setFeedbackLoading(false);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || `Erro ao ${feedbackActionType === 'complete' ? 'concluir' : feedbackActionType === 'start_progress' ? 'iniciar progresso' : 'cancelar'} tarefa`;
      message.error(errorMessage);
      setFeedbackLoading(false);
    }
  };

  const handleFeedbackCancel = () => {
    setFeedbackModalVisible(false);
    setFeedbackTaskId(null);
    setFeedbackTaskTitle('');
    setFeedbackLoading(false);
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      const response = await apiService.deleteTask(taskId);
      if (response.success) {
        message.success('Tarefa eliminada com sucesso!');
        fetchTasks();
        fetchStatistics();
      }
    } catch (error) {
      message.error('Erro ao eliminar tarefa');
    }
  };

  const handleViewTask = (record: Task) => {
    setSelectedTask(record);
    setViewModalVisible(true);
  };

  const handleEditTask = (record: Task) => {
    setSelectedTask(record);
    setEditModalVisible(true);
    
    // Convert date strings to dayjs objects for DatePicker
    const formValues = {
      ...record,
      time_limit: record.time_limit ? dayjs(record.time_limit) : undefined
    };
    
    editForm.setFieldsValue(formValues);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'green';
      case 'medium': return 'orange';
      case 'high': return 'red';
      case 'critical': return 'volcano';
      default: return 'blue';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'in_progress': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return <StarOutlined style={{ color: themeColors.success }} />;
      case 'normal': return <StarOutlined style={{ color: themeColors.info }} />;
      case 'hard': return <FireOutlined style={{ color: themeColors.warning }} />;
      case 'extreme': return <ThunderboltOutlined style={{ color: themeColors.danger }} />;
      default: return <StarOutlined style={{ color: themeColors.info }} />;
    }
  };

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case 'request': return <UserOutlined style={{ color: themeColors.info }} />;
      case 'challenge': return <TrophyOutlined style={{ color: themeColors.secondary }} />;
      case 'mission': return <RocketOutlined style={{ color: themeColors.primary }} />;
      default: return <UserOutlined style={{ color: themeColors.info }} />;
    }
  };

  const columns = [
    {
      title: '📋 Tarefa',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{record.description}</div>
          {record.lead_id && (
            <Tag color="blue" style={{ marginTop: 4 }}>
              <LinkOutlined /> Associado a Lead
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: '👤 Atribuição',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
                   render: (assignedTo: number, record: any) => {
        // First try to use the assignee_name from the task record
        if (record.assignee_name) {
          const user = availableUsers.find(u => u.id === assignedTo) || allUsers.find(u => u.id === assignedTo);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size="small" src={user?.avatar_url}>
                {record.assignee_name.charAt(0).toUpperCase()}
              </Avatar>
              <span>{record.assignee_name}</span>
            </div>
          );
        }
        
        // If not available, try to find in user arrays
        const user = availableUsers.find(u => u.id === assignedTo) || allUsers.find(u => u.id === assignedTo);
        return user ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar size="small" src={user.avatar_url}>
              {user.username.charAt(0).toUpperCase()}
            </Avatar>
            <span>{user.username}</span>
          </div>
        ) : 'Não atribuído';
      },
    },
    {
      title: '⚡ Prioridade',
      dataIndex: 'priority',
      key: 'priority',
             render: (priority: string) => {
         const priorityConfig = {
           medium: { color: 'orange', text: 'Médio' },
           high: { color: 'red', text: 'Alta' },
         };
         const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium;
         return (
           <Tag color={config.color}>
             {config.text}
           </Tag>
         );
       },
    },

         {
       title: '📊 Estado',
       dataIndex: 'status',
       key: 'status',
              render: (status: string) => {
                     const statusConfig = {
             pending: { color: 'blue', text: 'Pendente' },
             in_progress: { color: 'orange', text: 'Em Progresso' },
             completed: { color: 'green', text: 'Concluída' },
             cancelled: { color: 'red', text: 'Cancelada' },
           };
          const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
          return (
            <Tag color={config.color}>
              {config.text}
            </Tag>
          );
        },
     },
    {
      title: '⏰ Tempo',
      dataIndex: 'time_limit',
      key: 'time_limit',
      render: (timeLimit: string) => {
        if (!timeLimit) return 'Sem limite';
        const limit = new Date(timeLimit);
        const now = new Date();
        const isOverdue = limit < now;
        const timeLeft = limit.getTime() - now.getTime();
        const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
        
        return (
          <div>
            <div style={{ fontWeight: 'bold', color: isOverdue ? '#e74c3c' : '#2ecc71' }}>
              {isOverdue ? 'Expirado' : `${daysLeft} dias`}
            </div>
            <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
              {limit.toLocaleDateString('pt-PT')}
            </div>
          </div>
        );
      },
    },
         {
       title: '🔧 Ações',
       key: 'actions',
       render: (record: any) => (
         <Space>
           <Button
             type="text"
             icon={<EyeOutlined />}
             onClick={() => handleViewTask(record)}
             title="👁️ Ver Detalhes"
           />
           {(record.status === 'pending' || record.status === 'in_progress') && (
             <>
               <Button
                 type="text"
                 icon={<EditOutlined />}
                 onClick={() => handleEditTask(record)}
                 title="✏️ Editar"
               />
               {record.status === 'pending' && (
                 <Popconfirm
                   title="Confirmar Início de Progresso"
                   onConfirm={() => handleStartProgress(record.id)}
                   okText="Sim"
                   cancelText="Não"
                 >
                   <Button
                     type="text"
                     icon={<ClockCircleOutlined />}
                     title="🔄 Iniciar Progresso"
                     style={{ color: '#1890ff' }}
                   />
                 </Popconfirm>
               )}
               <Popconfirm
                 title="Confirmar Conclusão"
                 onConfirm={() => handleCompleteTask(record.id)}
                 okText="Sim"
                 cancelText="Não"
               >
                 <Button
                   type="text"
                   icon={<CheckOutlined />}
                   title="✅ Marcar como Concluída"
                   style={{ color: '#28a745' }}
                 />
               </Popconfirm>
               <Popconfirm
                 title="Confirmar Cancelamento"
                 onConfirm={() => handleCancelTask(record.id)}
                 okText="Sim"
                 cancelText="Não"
               >
                 <Button
                   type="text"
                   icon={<CloseOutlined />}
                   title="❌ Cancelar Tarefa"
                   style={{ color: '#dc3545' }}
                 />
               </Popconfirm>
             </>
           )}
           <Button
             type="text"
             icon={<DeleteOutlined />}
             onClick={() => handleDeleteTask(record.id)}
             title="🗑️ Eliminar"
             style={{ color: '#dc3545' }}
           />
         </Space>
       ),
     },
  ];

  return (
    <div className="taskmaster-container">
      {/* Header */}
      <div className="taskmaster-header">
        <div className="taskmaster-title">
          <CrownOutlined style={{ fontSize: 40, marginRight: 16 }} />
          <div>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 'bold' }}>Gestor de Tarefas</h1>
          </div>
        </div>
        <Button 
          type="primary" 
          size="large" 
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          className="taskmaster-create-btn"
        >
          ✨ Criar Nova Tarefa
        </Button>
      </div>

      {/* Filters */}
      <Card className="taskmaster-filters">
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '18px', fontWeight: '600' }}>
            🔍 Filtros de Tarefas
          </h3>
        </div>
        
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                📊 Estado da Tarefa
              </label>
            </div>
                         <Select
               placeholder="Selecione o estado da tarefa..."
               style={{ width: '100%' }}
               value={filters.status}
               onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
               allowClear
             >
                              <Option value="pending">⏳ Pendente</Option>
                <Option value="in_progress">🔄 Em Progresso</Option>
                <Option value="completed">✅ Concluída</Option>
                <Option value="cancelled">❌ Cancelada</Option>
             </Select>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                ⚡ Nível de Prioridade
              </label>
            </div>
            <Select
              placeholder="Selecione o nível de prioridade..."
              style={{ width: '100%' }}
              value={filters.priority}
              onChange={(value) => setFilters(prev => ({ ...prev, priority: value }))}
              allowClear
            >
               <Option value="medium">🟡 Médio</Option>
               <Option value="high">🟠 Alta</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                🎯 Tipo de Tarefa
              </label>
            </div>
            <Select
              placeholder="Selecione o tipo de tarefa..."
              style={{ width: '100%' }}
              value={filters.task_type}
              onChange={(value) => setFilters(prev => ({ ...prev, task_type: value }))}
              allowClear
            >
                             <Option value="request">📝 Pedido</Option>
               <Option value="challenge">🏆 Desafio</Option>
               <Option value="mission">🚀 Missão</Option>
            </Select>
          </Col>
        </Row>
        
        <Divider style={{ margin: '24px 0 16px 0' }} />
        
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: 0, color: '#2c3e50', fontSize: '16px', fontWeight: '600' }}>
            👤 Filtros de Atribuição
          </h4>
        </div>
        
                 <Row gutter={[16, 16]}>
           <Col xs={24} sm={12}>
             <Button 
               type={filters.assigned_to_me ? "primary" : "default"}
               onClick={() => setFilters(prev => ({ 
                 ...prev, 
                 assigned_to_me: !prev.assigned_to_me,
                 created_by_me: false // Reset other filter when this is toggled
               }))}
               className={filters.assigned_to_me ? "taskmaster-btn taskmaster-btn-primary" : "taskmaster-btn"}
               style={{ 
                 width: '100%', 
                 height: '48px',
                 fontSize: '14px',
                 fontWeight: '600'
               }}
             >
               {filters.assigned_to_me ? '✅ Mostrando: Minhas Tarefas' : '👤 Mostrar: Tarefas Atribuídas a Mim'}
             </Button>
           </Col>
           <Col xs={24} sm={12}>
             <Button 
               type={filters.created_by_me ? "primary" : "default"}
               onClick={() => setFilters(prev => ({ 
                 ...prev, 
                 created_by_me: !prev.created_by_me,
                 assigned_to_me: false // Reset other filter when this is toggled
               }))}
               className={filters.created_by_me ? "taskmaster-btn taskmaster-btn-primary" : "taskmaster-btn"}
               style={{ 
                 width: '100%', 
                 height: '48px',
                 fontSize: '14px',
                 fontWeight: '600'
               }}
             >
               {filters.created_by_me ? '✅ Mostrando: Tarefas Criadas por Mim' : '🎨 Mostrar: Tarefas que Eu Criei'}
             </Button>
           </Col>
         </Row>
        
        {(filters.assigned_to_me || filters.created_by_me || filters.status || filters.priority || filters.task_type) && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button 
              type="text"
              onClick={() => setFilters({
                status: '',
                priority: '',
                category: '',
                task_type: '',
                assigned_to_me: false,
                created_by_me: false
              })}
              style={{ color: '#667eea', fontWeight: '600' }}
            >
              🔄 Limpar Todos os Filtros
            </Button>
          </div>
        )}
      </Card>

      {/* Task Table */}
      <Card className="taskmaster-table">
        <Table
          dataSource={tasks}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} tarefas`,
            locale: {
              items_per_page: 'por página',
              jump_to: 'Ir para',
              jump_to_confirm: 'confirmar',
              page: 'Página',
              prev_page: 'Página anterior',
              next_page: 'Próxima página',
              prev_5: '5 páginas anteriores',
              next_5: '5 páginas seguintes',
              prev_3: '3 páginas anteriores',
              next_3: '3 páginas seguintes',
            }
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Nenhuma tarefa encontrada"
              />
            )
          }}
        />
      </Card>

      {/* Create Task Modal */}
      <Modal
        title="✨ Criar Nova Tarefa"
        open={createModalVisible}
        onCancel={handleCreateModalClose}
        footer={null}
        width={600}
        zIndex={999999}
        maskClosable={false}
        destroyOnClose={true}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateTask}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="📝 Título da Tarefa"
                rules={[{ required: true, message: 'Por favor, introduza o título da tarefa' }]}
              >
                <Input placeholder="Introduza o título da tarefa" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="task_type"
                label="🎯 Tipo de Tarefa"
                rules={[{ required: true, message: 'Por favor, selecione o tipo de tarefa' }]}
              >
                <Select placeholder="Selecione o tipo">
                  <Option value="request">📝 Pedido</Option>
                  <Option value="challenge">🏆 Desafio</Option>
                  <Option value="mission">🚀 Missão</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="📄 Descrição"
            rules={[{ required: true, message: 'Por favor, introduza a descrição da tarefa' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Descreva detalhadamente a tarefa a ser realizada..."
            />
          </Form.Item>

                     <Row gutter={16}>
             <Col span={12}>
               <Form.Item
                 name="priority"
                 label="⚡ Prioridade"
                 rules={[{ required: true, message: 'Por favor, selecione a prioridade' }]}
               >
                 <Select placeholder="Selecione a prioridade">
                   <Option value="medium">🟡 Médio</Option>
                   <Option value="high">🟠 Alta</Option>
                 </Select>
               </Form.Item>
             </Col>
             <Col span={12}>
               <Form.Item
                 name="category"
                 label="📂 Categoria"
                 rules={[{ required: true, message: 'Por favor, selecione a categoria' }]}
               >
                 <Select placeholder="Selecione a categoria">
                   <Option value="general">📋 Geral</Option>
                   <Option value="lead_followup">📞 Acompanhamento de Lead</Option>
                   <Option value="document">📄 Documento</Option>
                   <Option value="research">🔍 Investigação</Option>
                   <Option value="other">📌 Outro</Option>
                 </Select>
               </Form.Item>
             </Col>
           </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="assigned_to"
                label="👤 Atribuir a"
                rules={[{ required: true, message: 'Por favor, selecione um utilizador' }]}
              >
                <Select 
                  placeholder="Selecione um utilizador"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                  }
                >
                  {allUsers.map(user => (
                    <Option key={user.id} value={user.id}>
                      👤 {user.username}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="time_limit"
                label="⏰ Prazo Limite"
              >
                <DatePicker 
                  showTime={{ 
                    format: 'HH:mm',
                    hideDisabledOptions: true,
                    minuteStep: 1
                  }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Selecione data e hora"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

                     <Row gutter={16}>
             <Col span={24}>
               <Form.Item
                 name="lead_id"
                 label="🔗 Associar a Lead (Opcional)"
               >
                 <Select 
                   placeholder="Selecione um lead"
                   allowClear
                   showSearch
                   optionFilterProp="children"
                 >
                                       {leads.map(lead => (
                      <Option key={lead.id} value={lead.id}>
                        <span style={getLeadCategoryStyle(lead.lead_category)}>👤 {lead.name || `${lead.first_name} ${lead.last_name}`}</span> {lead.phone && `(${lead.phone})`}
                      </Option>
                    ))}
                 </Select>
               </Form.Item>
             </Col>
           </Row>

          <Form.Item style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
                // Call the onTaskCreated callback if provided (for modal mode)
                if (onTaskCreated && isModal) {
                  onTaskCreated();
                }
              }} className="taskmaster-btn">
                ❌ Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={createLoading} className="taskmaster-btn taskmaster-btn-primary">
                ✨ Criar Tarefa
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Task Modal */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <EditOutlined style={{ fontSize: '24px', color: '#667eea', marginRight: '8px' }} />
            ✏️ Editar Tarefa
          </div>
        }
        open={editModalVisible}
        onCancel={handleEditModalClose}
        footer={null}
        width={600}
        zIndex={999999}
        maskClosable={false}
        destroyOnClose={true}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateTask}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="📝 Título da Tarefa"
                rules={[{ required: true, message: 'Por favor, introduza o título da tarefa' }]}
              >
                <Input placeholder="Introduza o título da tarefa" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="⚡ Prioridade"
              >
                <Select>
                  <Option value="medium">🟡 Médio</Option>
                  <Option value="high">🟠 Alta</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="📄 Descrição"
            rules={[{ required: true, message: 'Por favor, introduza a descrição da tarefa' }]}
          >
            <TextArea rows={4} placeholder="Descreva detalhadamente a tarefa..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="category"
                label="📂 Categoria"
              >
                <Select>
                  <Option value="general">📋 Geral</Option>
                  <Option value="lead_followup">📞 Acompanhamento de Lead</Option>
                  <Option value="document">📄 Documento</Option>
                  <Option value="research">🔍 Investigação</Option>
                  <Option value="other">📌 Outro</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="📊 Estado"
              >
                                 <Select>
                   <Option value="pending">⏳ Pendente</Option>
                   <Option value="in_progress">🔄 Em Progresso</Option>
                   <Option value="completed">✅ Concluída</Option>
                   <Option value="cancelled">❌ Cancelada</Option>
                 </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="assigned_to"
                label="👤 Atribuir a"
              >
                <Select 
                  placeholder="Selecione um utilizador"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                  }
                >
                  {allUsers.map(user => (
                    <Option key={user.id} value={user.id}>
                      👤 {user.username}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="time_limit"
                label="⏰ Prazo Limite"
              >
                <DatePicker 
                  showTime={{ 
                    format: 'HH:mm',
                    hideDisabledOptions: true,
                    minuteStep: 1
                  }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Selecione data e hora"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: 'center', marginTop: '24px' }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                className="taskmaster-btn taskmaster-btn-primary"
                style={{ 
                  height: '40px',
                  fontSize: '16px'
                }}
              >
                ✅ Atualizar Tarefa
              </Button>
              <Button 
                onClick={() => {
                  setEditModalVisible(false);
                  setSelectedTask(null);
                  editForm.resetFields();
                }}
                className="taskmaster-btn"
              >
                ❌ Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
             </Modal>
 
                       {/* View Task Modal */}
        <Modal
          title={
            <div style={{ textAlign: 'center', fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
              <EyeOutlined style={{ fontSize: '20px', color: '#13c2c2', marginRight: '8px' }} />
              Detalhes da Tarefa
            </div>
          }
          open={viewModalVisible}
          onCancel={handleViewModalClose}
          footer={[
            <Button 
              key="edit" 
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                setViewModalVisible(false);
                handleEditTask(selectedTask!);
              }}
              style={{ marginRight: 8 }}
            >
              Editar
            </Button>,
            <Button 
              key="close" 
              onClick={() => {
                setViewModalVisible(false);
                setSelectedTask(null);
              }}
            >
              Fechar
            </Button>
          ]}
          width={700}
          zIndex={999999}
          maskClosable={false}
          destroyOnClose={true}
        >
          {selectedTask && (
            <div style={{ padding: '8px 0' }}>
                             {/* Header Section */}
               <div style={{ 
                 background: '#f8f9fa', 
                 padding: '20px', 
                 borderRadius: '6px',
                 border: '1px solid #e9ecef',
                 marginBottom: '24px'
               }}>
                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                   <div>
                     <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#2c3e50' }}>
                       {selectedTask.title}
                     </h2>
                     <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#495057' }}>
                       {selectedTask.task_type === 'request' ? 'Pedido' : 
                        selectedTask.task_type === 'challenge' ? 'Desafio' : 'Missão'}
                     </p>
                   </div>
                   <div style={{ textAlign: 'right' }}>
                     <Tag color={
                       selectedTask.priority === 'medium' ? 'orange' :
                       selectedTask.priority === 'high' ? 'red' : 'orange'
                     } style={{ marginBottom: 8, fontSize: '12px' }}>
                       {selectedTask.priority === 'medium' ? 'Médio' :
                        selectedTask.priority === 'high' ? 'Alta' : 'Médio'}
                     </Tag>
                     <br />
                                                                     <Tag color={
                           selectedTask.status === 'pending' ? 'blue' :
                           selectedTask.status === 'in_progress' ? 'orange' :
                           selectedTask.status === 'completed' ? 'green' : 'red'
                         } style={{ fontSize: '12px' }}>
                           {selectedTask.status === 'pending' ? 'Pendente' :
                            selectedTask.status === 'in_progress' ? 'Em Progresso' :
                            selectedTask.status === 'completed' ? 'Concluída' : 'Cancelada'}
                         </Tag>
                   </div>
                 </div>
               </div>

              {/* Description */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#2c3e50', fontSize: '16px', fontWeight: '600' }}>
                  Descrição
                </h4>
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '6px', 
                  border: '1px solid #e9ecef',
                  lineHeight: '1.6',
                  fontSize: '14px',
                  color: '#495057'
                }}>
                  {selectedTask.description}
                </div>
              </div>

              {/* Details Grid */}
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '16px', 
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', fontWeight: '600' }}>
                      Categoria
                    </h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                      {selectedTask.category === 'general' ? 'Geral' :
                       selectedTask.category === 'lead_followup' ? 'Acompanhamento de Lead' :
                       selectedTask.category === 'document' ? 'Documento' :
                       selectedTask.category === 'research' ? 'Investigação' : 'Outro'}
                    </p>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '16px', 
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', fontWeight: '600' }}>
                      Prazo Limite
                    </h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                      {selectedTask.time_limit ? 
                        new Date(selectedTask.time_limit).toLocaleString('pt-PT', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 
                        'Sem limite'}
                    </p>
                  </div>
                </Col>
              </Row>

              {/* Assignment */}
              <div style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '6px',
                border: '1px solid #e9ecef',
                marginTop: '16px'
              }}>
                <h5 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', fontWeight: '600' }}>
                  Atribuído a
                </h5>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {selectedTask.assignee_name ? (
                    <>
                      <Avatar 
                        size={40} 
                        src={availableUsers.find(u => u.id === selectedTask.assigned_to)?.avatar_url}
                        style={{ backgroundColor: '#667eea' }}
                      >
                        {selectedTask.assignee_name.charAt(0).toUpperCase()}
                      </Avatar>
                                             <div>
                         <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                           {selectedTask.assignee_name}
                         </div>
                       </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar size={40} style={{ backgroundColor: '#6c757d' }}>
                        ?
                      </Avatar>
                      <span style={{ fontSize: '14px', color: '#6c757d' }}>Não atribuído</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Lead Association */}
              {selectedTask.lead_id && (
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '6px',
                  border: '1px solid #e9ecef',
                  marginTop: '16px'
                }}>
                  <h5 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', fontWeight: '600' }}>
                    Lead Associado
                  </h5>
                                     <p style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                     {selectedTask.lead_name || `Lead ID: ${selectedTask.lead_id}`}
                     {selectedTask.lead_phone && ` (${selectedTask.lead_phone})`}
                   </p>
                </div>
              )}

              {/* Feedback (for completed/cancelled tasks) */}
              {(selectedTask.status === 'completed' || selectedTask.status === 'cancelled') && (
                <>
                  <div style={{ marginTop: '16px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#2c3e50', fontSize: '16px', fontWeight: '600' }}>
                      📝 Feedback
                    </h4>
                    <div style={{ 
                      background: selectedTask.status === 'completed' ? '#f0f9ff' : '#fef2f2', 
                      padding: '16px', 
                      borderRadius: '6px', 
                      border: `1px solid ${selectedTask.status === 'completed' ? '#bae6fd' : '#fecaca'}`,
                      lineHeight: '1.6',
                      fontSize: '14px',
                      color: '#495057'
                    }}>
                      {selectedTask.feedback || 'Nenhum feedback fornecido'}
                    </div>
                  </div>
                  
                  {selectedTask.completed_at && (
                    <div style={{ marginTop: '16px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#2c3e50', fontSize: '16px', fontWeight: '600' }}>
                        {selectedTask.status === 'completed' ? '✅ Concluída em' : '❌ Cancelada em'}
                      </h4>
                      <div style={{ 
                        background: '#f8f9fa', 
                        padding: '16px', 
                        borderRadius: '6px', 
                        border: '1px solid #e9ecef'
                      }}>
                        <p style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                          {new Date(selectedTask.completed_at).toLocaleString('pt-PT', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {selectedTask.completer_name && (
                            <span style={{ marginLeft: '8px', color: '#6c757d' }}>
                              por {selectedTask.completer_name}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Timestamps */}
              <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col span={12}>
                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '16px', 
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', fontWeight: '600' }}>
                      Criado em
                    </h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                      {new Date(selectedTask.created_at).toLocaleString('pt-PT', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '16px', 
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#2c3e50', fontSize: '14px', fontWeight: '600' }}>
                      Atualizado em
                    </h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                      {new Date(selectedTask.updated_at).toLocaleString('pt-PT', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Modal>

        {/* Task Feedback Modal */}
        <TaskFeedbackModal
          visible={feedbackModalVisible}
          onCancel={handleFeedbackCancel}
          onSubmit={handleFeedbackSubmit}
          taskTitle={feedbackTaskTitle}
          actionType={feedbackActionType}
          loading={feedbackLoading}
        />
       
    </div>
  );
};

export default TaskMaster; 